import React, { useEffect, useMemo, useRef, useState } from 'react';

import { DndContext, type DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSnackbar } from 'notistack';

import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';

import { isAccommodationPackage } from '~/components/Offers/Detail/typeNarrowingUtils';

import type { PackageInclusionsByPackageId } from '~/hooks/apis/inclusions/useGetPackageInclusionsByOffer';
import { useSaveOfferPackage } from '~/hooks/apis/offers/useSaveOfferPackage';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import ContentActionBar, { CONTENT_ACTION_BUTTON_SX } from '../ContentActionBar';
import type { PropertyRoomTypeWithImages } from '../ImagesVideo/types';
import { type MasterInclusion, buildPackageInclusionsFromMaster, getPackageOptions } from '../Inclusions/utils';
import {
  type PackageInclusionRecord,
  getInclusionPersistId,
  getPackageInclusionSortOrder,
  hasUnsavedChanges as hasUnsavedContentChanges,
  isInclusionHidden,
} from '../utils';

import SortableOfferPackageRow from './SortableOfferPackageRow';
import { buildAutofillPackageValues, matchPackageRoomTypeWithAi } from './autofillPackage';
import { cleanPackageDisplayNameWithAi } from './packageDisplayName';
import { DEFAULT_PACKAGE_TAB } from './types';
import type { OfferBuilderPackage, PackageTab } from './types';
import {
  buildOfferBuilderPackages,
  buildPackageSavePayload,
  getMissingRequiredPackageFields,
  getPackageCampaignCode,
  getPackageInclusions,
  getPackageName,
  getPackagePersistId,
  getPackageStatus,
} from './utils';

interface Props {
  isLoadingOffer?: boolean;
  masterInclusions?: Array<MasterInclusion>;
  offerId?: string;
  offerError?: unknown;
  offerType?: App.OfferType;
  packageInclusions?: PackageInclusionsByPackageId;
  packageCopyVersion?: number;
  packages?: Array<App.Package>;
  property?: App.Property;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  vendorId?: string;
  onOpenInclusions: () => void;
}

const EMPTY_PACKAGES: Array<App.Package> = [];

type OfferPackageWithContentFields = App.Package & {
  inclusion_highlights?: string;
  sort_order?: number;
};

function getPackageKey(offerPackages: Array<App.Package>) {
  return JSON.stringify({
    packages: offerPackages.map((offerPackage) => {
      const packageRecord = offerPackage as OfferPackageWithContentFields;

      return {
        id: offerPackage.id_salesforce_external ?? offerPackage.le_package_id,
        description: offerPackage.description,
        inclusionHighlights: packageRecord.inclusion_highlights,
        name: offerPackage.deal_option_name ?? offerPackage.name,
        regions: offerPackage.regions,
        roomOccupancy: offerPackage.room_occupancy,
        sortOrder: packageRecord.sort_order,
        status: offerPackage.status,
      };
    }),
  });
}

function getPackageIdentityKey(offerPackages: Array<App.Package>) {
  return JSON.stringify({
    packages: offerPackages.map((offerPackage) => ({
      id: offerPackage.id_salesforce_external ?? offerPackage.le_package_id,
      name: offerPackage.deal_option_name ?? offerPackage.name,
    })),
  });
}

function getInclusionLookupKey(inclusion: PackageInclusionRecord) {
  const persistId = getInclusionPersistId(inclusion);

  if (persistId) {
    return `id:${persistId}`;
  }

  const name = inclusion.inclusion?.name ?? inclusion.name ?? inclusion.content;

  return name ? `name:${name.trim().toLowerCase()}` : undefined;
}

function buildPackageInclusionLookup(inclusions?: Array<PackageInclusionRecord>) {
  return (inclusions ?? []).reduce<Map<string, PackageInclusionRecord>>((inclusionsByKey, inclusion) => {
    const key = getInclusionLookupKey(inclusion);

    if (key && !inclusionsByKey.has(key)) {
      inclusionsByKey.set(key, inclusion);
    }

    return inclusionsByKey;
  }, new Map());
}

function applyPackageInclusions(
  masterInclusions: Array<PackageInclusionRecord>,
  salesforcePackageInclusions?: Array<PackageInclusionRecord>,
  usePackageOrder = false,
) {
  const packageInclusionsByKey = buildPackageInclusionLookup(salesforcePackageInclusions);
  const appliedMasterInclusions = masterInclusions.map((masterInclusion) => {
    const key = getInclusionLookupKey(masterInclusion);
    const packageInclusion = key ? packageInclusionsByKey.get(key) : undefined;

    if (!packageInclusion) {
      return masterInclusion;
    }

    return {
      ...packageInclusion,
      ...masterInclusion,
      id: packageInclusion.id ?? masterInclusion.id,
      inclusion_id: packageInclusion.inclusion_id ?? masterInclusion.inclusion_id,
      inclusion: {
        ...packageInclusion.inclusion,
        ...masterInclusion.inclusion,
        id: masterInclusion.inclusion?.id ?? packageInclusion.inclusion?.id,
      },
      sort_order: usePackageOrder ? packageInclusion.sort_order : masterInclusion.sort_order,
      style: usePackageOrder ? packageInclusion.style : masterInclusion.style,
      lux_plus_tier: usePackageOrder ? packageInclusion.lux_plus_tier : masterInclusion.lux_plus_tier,
    };
  });
  const hiddenInclusions = salesforcePackageInclusions ? salesforcePackageInclusions.filter(isInclusionHidden) : [];

  const visibleInclusions = usePackageOrder
    ? appliedMasterInclusions
        .slice()
        .sort((a, b) => (getPackageInclusionSortOrder(a) ?? Infinity) - (getPackageInclusionSortOrder(b) ?? Infinity))
    : appliedMasterInclusions;

  return visibleInclusions.concat(hiddenInclusions);
}

export function mergePackageInclusions(
  offerPackages: Array<App.Package>,
  packageInclusionsByPackageId: Record<string, Array<PackageInclusionRecord>> | undefined,
  packageInclusions?: PackageInclusionsByPackageId,
  // Default keeps master order for Inclusions tab saves; package rows pass true to keep package order.
  usePackageOrder = false,
) {
  const packageOptions = getPackageOptions(offerPackages);

  return offerPackages.map((offerPackage, index) => {
    const packageId = getPackagePersistId(offerPackage) ?? packageOptions[index]?.id ?? `package-${index}`;
    const salesforcePackageInclusions = packageInclusions?.[packageId];
    const masterInclusions = packageInclusionsByPackageId?.[packageId];
    const inclusions = masterInclusions
      ? applyPackageInclusions(masterInclusions, salesforcePackageInclusions, usePackageOrder)
      : salesforcePackageInclusions;

    if (!inclusions) {
      return offerPackage;
    }

    return {
      ...offerPackage,
      inclusions,
    };
  });
}

function getAvailabilityRatesUrl(offerPackage: App.Package, vendorId?: string) {
  if (
    !vendorId ||
    !isAccommodationPackage(offerPackage) ||
    !offerPackage.fk_property_id ||
    !offerPackage.fk_room_type_id ||
    !offerPackage.fk_room_rate_id
  ) {
    return undefined;
  }

  return `/vendors/${vendorId}/properties/${offerPackage.fk_property_id}/room-types/${offerPackage.fk_room_type_id}/room-rates/${offerPackage.fk_room_rate_id}`;
}

export function normalizePackagesForComparison(offerPackages: Array<OfferBuilderPackage>) {
  return offerPackages.map((offerPackage) => ({
    ...offerPackage,
    package: {
      ...offerPackage.package,
      campaign_code: getPackageCampaignCode(offerPackage.package),
      fk_room_rate_id:
        'fk_room_rate_id' in offerPackage.package && offerPackage.package.fk_room_rate_id
          ? String(offerPackage.package.fk_room_rate_id)
          : undefined,
      fk_room_type_id:
        'fk_room_type_id' in offerPackage.package && offerPackage.package.fk_room_type_id
          ? String(offerPackage.package.fk_room_type_id)
          : undefined,
      regions: Array.isArray(offerPackage.package.regions) ? [...offerPackage.package.regions].sort() : undefined,
    },
  }));
}

function applyMasterInclusionsToPackages(
  offerPackages: Array<OfferBuilderPackage>,
  packageInclusionsByPackageId: Record<string, Array<PackageInclusionRecord>>,
  packageInclusions?: PackageInclusionsByPackageId,
) {
  return offerPackages.map((offerPackage) => {
    const packageId = getPackagePersistId(offerPackage.package);
    const masterInclusions = packageId ? packageInclusionsByPackageId[packageId] : undefined;

    if (!masterInclusions) {
      return offerPackage;
    }

    const salesforcePackageInclusions = packageId ? packageInclusions?.[packageId] : undefined;
    const currentPackageInclusions = salesforcePackageInclusions ?? getPackageInclusions(offerPackage.package);

    return {
      ...offerPackage,
      package: {
        ...offerPackage.package,
        inclusions: applyPackageInclusions(masterInclusions, currentPackageInclusions, true),
      },
    };
  });
}

export default function OfferPackages({
  isLoadingOffer,
  masterInclusions,
  offerId,
  offerError,
  offerType,
  packageInclusions,
  packageCopyVersion,
  packages,
  property,
  propertyRoomTypes,
  vendorId,
  onOpenInclusions,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const offerPackages = packages ?? EMPTY_PACKAGES;
  const packageInclusionsByPackageId = useMemo(() => {
    return masterInclusions
      ? buildPackageInclusionsFromMaster(masterInclusions, getPackageOptions(offerPackages))
      : undefined;
  }, [masterInclusions, offerPackages]);
  const initialPackages = useMemo(() => {
    return buildOfferBuilderPackages(
      mergePackageInclusions(offerPackages, packageInclusionsByPackageId, packageInclusions, true),
    );
  }, [packageInclusions, packageInclusionsByPackageId, offerPackages]);
  const [orderedPackages, setOrderedPackages] = useState<Array<OfferBuilderPackage>>([]);
  const [expandedPackageIds, setExpandedPackageIds] = useState<Array<string>>([]);
  const [activePackageTabs, setActivePackageTabs] = useState<Record<string, PackageTab>>({});
  const [cleaningDisplayNamePackageId, setCleaningDisplayNamePackageId] = useState<string>();
  const [autofillingPackageId, setAutofillingPackageId] = useState<string>();
  const [savingPackageId, setSavingPackageId] = useState<string>();
  const [saveAllProgress, setSaveAllProgress] = useState<{ completed: number; total: number }>();
  const [savedOrderedPackages, setSavedOrderedPackages] = useState<Array<OfferBuilderPackage>>([]);
  const saveOfferPackage = useSaveOfferPackage();
  const packageStateKey = useMemo(() => {
    return JSON.stringify({
      copyVersion: packageCopyVersion,
      state: getPackageKey(offerPackages),
    });
  }, [packageCopyVersion, offerPackages]);
  const packageIdentityKey = useMemo(() => getPackageIdentityKey(offerPackages), [offerPackages]);
  const previousPackageStateKey = useRef<string | undefined>(undefined);
  const previousPackageIdentityKey = useRef<string | undefined>(undefined);
  const isSavingAllPackagesRef = useRef(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const packageSortableIds = orderedPackages.map((offerPackage) => offerPackage.sortableId);
  const rowPackages = orderedPackages.map((orderedPackage) => orderedPackage.package);
  const isSavingPackages = !!savingPackageId || !!saveAllProgress;
  const hasUnsavedChanges = hasUnsavedContentChanges(
    orderedPackages,
    savedOrderedPackages,
    normalizePackagesForComparison,
  );

  useEffect(() => {
    if (previousPackageStateKey.current === packageStateKey) {
      return;
    }

    previousPackageStateKey.current = packageStateKey;
    setOrderedPackages(initialPackages);
    setSavedOrderedPackages(initialPackages);

    if (previousPackageIdentityKey.current !== packageIdentityKey) {
      previousPackageIdentityKey.current = packageIdentityKey;
      setExpandedPackageIds([]);
      setActivePackageTabs({});
    }
  }, [initialPackages, packageIdentityKey, packageStateKey]);

  useEffect(() => {
    if (!packageInclusionsByPackageId) {
      return;
    }

    setOrderedPackages((currentPackages) =>
      applyMasterInclusionsToPackages(currentPackages, packageInclusionsByPackageId, packageInclusions),
    );
    setSavedOrderedPackages((currentPackages) =>
      applyMasterInclusionsToPackages(currentPackages, packageInclusionsByPackageId, packageInclusions),
    );
  }, [packageInclusions, packageInclusionsByPackageId]);

  const areAllPackagesExpanded =
    orderedPackages.length > 0 &&
    orderedPackages.every((offerPackage) => expandedPackageIds.includes(offerPackage.sortableId));

  const handleToggleAllPackages = () => {
    setExpandedPackageIds(areAllPackagesExpanded ? [] : orderedPackages.map((offerPackage) => offerPackage.sortableId));
  };

  const handleExpandedChange = (packageId: string, isExpanded: boolean) => {
    if (isExpanded) {
      setActivePackageTabs((currentTabs) => ({
        ...currentTabs,
        [packageId]: currentTabs[packageId] ?? DEFAULT_PACKAGE_TAB,
      }));
    }

    setExpandedPackageIds((currentPackageIds) =>
      isExpanded
        ? Array.from(new Set(currentPackageIds.concat(packageId)))
        : currentPackageIds.filter((currentPackageId) => currentPackageId !== packageId),
    );
  };

  const handlePackageChange = (
    packageId: string,
    nextPackageValues: Partial<App.Package> & Record<string, unknown>,
  ) => {
    setOrderedPackages((currentPackages) =>
      currentPackages.map((offerPackage) => {
        if (offerPackage.sortableId !== packageId) {
          return offerPackage;
        }

        const nextPackage = { ...offerPackage.package, ...nextPackageValues };
        const status = getPackageStatus(nextPackage);

        return {
          ...offerPackage,
          package: nextPackage,
          // Keep the header summary stable while editing fields that affect its text.
          packageSummary: offerPackage.packageSummary,
          statusColor: status.color,
          statusLabel: status.label,
        };
      }),
    );
  };

  const handleAutofill = async (packageId: string) => {
    if (autofillingPackageId) {
      return;
    }

    const targetPackage = orderedPackages.find((offerPackage) => offerPackage.sortableId === packageId)?.package;

    if (!targetPackage) {
      return;
    }

    setAutofillingPackageId(packageId);

    try {
      const autofillValues = await buildAutofillPackageValues({
        offerPackage: targetPackage,
        property,
        offerType,
      });

      if (autofillValues) {
        handlePackageChange(packageId, autofillValues);
        enqueueSnackbar(
          `Package autofilled from ${autofillValues.name} - please verify the room type before approving`,
          {
            variant: 'success',
          },
        );
        return;
      }

      enqueueSnackbar('No matching flash room type and rate found for this deal option', { variant: 'warning' });
    } catch (error) {
      reportError(error);
      enqueueSnackbar(getErrorMessage(error, 'Failed to autofill package'), { variant: 'error' });
    } finally {
      setAutofillingPackageId(undefined);
    }
  };

  const handleCleanDisplayName = async (packageId: string) => {
    if (cleaningDisplayNamePackageId) {
      return;
    }

    const targetPackage = orderedPackages.find((offerPackage) => offerPackage.sortableId === packageId);

    if (!targetPackage) {
      return;
    }

    setCleaningDisplayNamePackageId(packageId);

    try {
      const matchedRoomType = await matchPackageRoomTypeWithAi({
        offerPackage: targetPackage.package,
        packageName: targetPackage.originalPackageName,
        property,
      });
      const displayName = await cleanPackageDisplayNameWithAi({
        currentDisplayName: getPackageName(targetPackage.package),
        originalPackageName: targetPackage.originalPackageName,
        roomName: matchedRoomType?.name,
      });

      if (!displayName) {
        throw new Error('No display name returned.');
      }

      handlePackageChange(packageId, { name: displayName });
      enqueueSnackbar('Package display name cleaned up', { variant: 'success' });
    } catch (error) {
      reportError(error);
      enqueueSnackbar(getErrorMessage(error, 'Failed to clean up package display name'), { variant: 'error' });
    } finally {
      setCleaningDisplayNamePackageId(undefined);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setOrderedPackages((currentPackages) => {
      const oldIndex = currentPackages.findIndex((offerPackage) => offerPackage.sortableId === active.id);
      const newIndex = currentPackages.findIndex((offerPackage) => offerPackage.sortableId === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return currentPackages;
      }

      return arrayMove(currentPackages, oldIndex, newIndex);
    });
  };

  const savePackage = async (offerPackage: OfferBuilderPackage, sortOrder?: number) => {
    const payloadPackageId = getPackagePersistId(offerPackage.package);
    const payload = buildPackageSavePayload({
      offerId,
      offerPackage: offerPackage.package,
      savedPackageInclusions: payloadPackageId ? packageInclusions?.[payloadPackageId] : undefined,
      sortOrder,
      vendorId,
    });

    if (!payload.offerId || !payload.packageId) {
      throw new Error('Unable to save package without an offer ID and package ID.');
    }

    await saveOfferPackage.mutateAsync({
      offerId: payload.offerId,
      packageData: payload.packageData,
      packageId: payload.packageId,
      packageInclusions: payload.packageInclusions,
    });
  };

  const handleSavePackage = async (offerPackage: OfferBuilderPackage) => {
    setSavingPackageId(offerPackage.sortableId);

    try {
      await savePackage(offerPackage);
      setSavedOrderedPackages((currentPackages) =>
        currentPackages.map((currentPackage) =>
          currentPackage.sortableId === offerPackage.sortableId ? offerPackage : currentPackage,
        ),
      );
      enqueueSnackbar(`${offerPackage.name} saved`, { variant: 'success' });
    } catch (error) {
      reportError(error);
      enqueueSnackbar(getErrorMessage(error, `Failed to save ${offerPackage.name}`), { variant: 'error' });
    } finally {
      setSavingPackageId(undefined);
    }
  };

  const handleSaveAllPackages = async () => {
    if (isSavingAllPackagesRef.current) {
      return;
    }

    isSavingAllPackagesRef.current = true;
    setSaveAllProgress({ completed: 0, total: orderedPackages.length });
    let savedPackageCount = 0;
    let skippedPackageCount = 0;
    let failedPackageCount = 0;

    try {
      for (const [index, offerPackage] of orderedPackages.entries()) {
        setSavingPackageId(offerPackage.sortableId);

        try {
          if (
            getMissingRequiredPackageFields(offerPackage.package).length > 0 ||
            !offerId ||
            !getPackagePersistId(offerPackage.package)
          ) {
            skippedPackageCount += 1;
          } else {
            await savePackage(offerPackage, index + 1);
            savedPackageCount += 1;
          }
        } catch (error) {
          failedPackageCount += 1;
          reportError(error);
        } finally {
          setSaveAllProgress({ completed: index + 1, total: orderedPackages.length });
        }
      }

      if (failedPackageCount > 0 || skippedPackageCount > 0) {
        const saveAllResultMessage = [
          savedPackageCount > 0 && `${savedPackageCount} package${savedPackageCount === 1 ? '' : 's'} saved`,
          skippedPackageCount > 0 && `${skippedPackageCount} skipped`,
          failedPackageCount > 0 && `${failedPackageCount} failed`,
        ]
          .filter(Boolean)
          .join('. ');

        enqueueSnackbar(`${saveAllResultMessage}.`, { variant: 'warning' });
        return;
      }

      enqueueSnackbar('All packages saved', { variant: 'success' });
      setSavedOrderedPackages(orderedPackages);
    } finally {
      isSavingAllPackagesRef.current = false;
      setSavingPackageId(undefined);
      setSaveAllProgress(undefined);
    }
  };

  if (offerError) {
    return <Alert severity="error">Unable to load offer packages.</Alert>;
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">Offer packages</Typography>
          <Typography variant="body1" color="text.secondary">
            Order packages from base to most expensive. Verify each package is listed on the hotel's website.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleToggleAllPackages}
            disabled={orderedPackages.length === 0}
          >
            {areAllPackagesExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="body2" color="text.secondary">
            <Box component="span" sx={{ fontWeight: 700 }}>
              {orderedPackages.length}
            </Box>{' '}
            {orderedPackages.length === 1 ? 'package' : 'packages'} · drag{' '}
            <Box component="span" sx={{ color: 'text.disabled' }}>
              ::
            </Box>{' '}
            to reorder
          </Typography>
        </Stack>
      </Paper>

      {orderedPackages.length === 0 && !isLoadingOffer && (
        <Alert severity="info">No packages are currently available for this offer.</Alert>
      )}

      <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={packageSortableIds} strategy={verticalListSortingStrategy}>
            <Stack spacing={2}>
              {orderedPackages.map((offerPackage) => {
                const activePackageTab = activePackageTabs[offerPackage.sortableId] ?? DEFAULT_PACKAGE_TAB;

                return (
                  <SortableOfferPackageRow
                    key={offerPackage.sortableId}
                    activePackageTab={activePackageTab}
                    expanded={expandedPackageIds.includes(offerPackage.sortableId)}
                    isAutofillDisabled={!!autofillingPackageId}
                    isAutofilling={autofillingPackageId === offerPackage.sortableId}
                    isCleanDisplayNameDisabled={!!cleaningDisplayNamePackageId}
                    isCleaningDisplayName={cleaningDisplayNamePackageId === offerPackage.sortableId}
                    isSaveDisabled={isSavingPackages}
                    isSaving={savingPackageId === offerPackage.sortableId}
                    offerId={offerId}
                    offerPackage={offerPackage}
                    availabilityRatesUrl={getAvailabilityRatesUrl(offerPackage.package, vendorId)}
                    onAutofill={() => void handleAutofill(offerPackage.sortableId)}
                    onCleanDisplayName={() => void handleCleanDisplayName(offerPackage.sortableId)}
                    onOpenInclusions={onOpenInclusions}
                    packages={activePackageTab === 'Package inclusions' ? rowPackages : undefined}
                    propertyRoomTypes={propertyRoomTypes}
                    vendorId={vendorId}
                    onChange={(nextPackageValues) => handlePackageChange(offerPackage.sortableId, nextPackageValues)}
                    onExpandedChange={(isExpanded) => handleExpandedChange(offerPackage.sortableId, isExpanded)}
                    onSave={() => void handleSavePackage(offerPackage)}
                    onTabChange={(tab) =>
                      setActivePackageTabs((currentTabs) => ({ ...currentTabs, [offerPackage.sortableId]: tab }))
                    }
                  />
                );
              })}
            </Stack>
          </SortableContext>
        </DndContext>
      </Box>

      <ContentActionBar
        hasUnsavedChanges={hasUnsavedChanges}
        isSaving={isSavingPackages}
        tabName="Offer packages"
        onDiscard={() => {
          setOrderedPackages(savedOrderedPackages);
        }}
      >
        <Button
          variant="contained"
          disabled={orderedPackages.length === 0 || isSavingPackages}
          onClick={() => void handleSaveAllPackages()}
          sx={CONTENT_ACTION_BUTTON_SX}
        >
          {saveAllProgress
            ? `Saving ${Math.min(saveAllProgress.completed + 1, saveAllProgress.total)}/${saveAllProgress.total}`
            : 'Save all'}
        </Button>
      </ContentActionBar>
    </Box>
  );
}
